import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, track } from "@/lib/api";
import { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { MapPin, Video, Users, CheckCircle2, Globe, Mail, Phone, Calendar, Filter, Loader2, Search, X } from "lucide-react";

function TherapistCard({ t }) {
  const modality = (t.modality || (t.telehealth ? "telehealth" : "")).toLowerCase();
  const states = t.states || (t.state ? [t.state] : []);
  const booking = t.booking_url || t.website;
  return (
    <div className="bg-white rounded-3xl p-6 card-soft flex flex-col h-full" data-testid={`therapist-card-${t.id}`}>
      <div className="flex items-start gap-4">
        {t.photo ? (
          <img src={mediaUrl(t.photo)} alt={t.name} className="w-16 h-16 rounded-2xl object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-[#291B2D] text-[#C89B55] flex items-center justify-center font-heading text-xl">
            {(t.name || "T").slice(0, 1)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <Link to={`/therapists/${t.slug || t.id}`} className="font-heading text-lg leading-tight hover:text-[#C89B55] transition-smooth" data-testid={`therapist-name-${t.id}`}>{t.name}</Link>
          {t.credentials && <p className="text-xs text-[#291B2D]/55 mt-0.5">{t.credentials}</p>}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(modality === "both" || modality === "telehealth") && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#C89B55]/15 text-[#291B2D] rounded-full px-2 py-0.5"><Video size={10} /> Telehealth</span>
            )}
            {(modality === "both" || modality === "in-person") && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#F7F2EA] text-[#291B2D]/70 rounded-full px-2 py-0.5"><MapPin size={10} /> In-person</span>
            )}
            {t.faith_informed && <span className="text-[10px] uppercase tracking-wide bg-[#F7F2EA] text-[#291B2D]/70 rounded-full px-2 py-0.5">Faith-informed</span>}
            {t.trauma_informed && <span className="text-[10px] uppercase tracking-wide bg-[#F7F2EA] text-[#291B2D]/70 rounded-full px-2 py-0.5">Trauma-informed</span>}
          </div>
        </div>
      </div>

      {t.bio && <p className="text-sm text-[#291B2D]/70 mt-4 leading-relaxed line-clamp-4">{t.bio}</p>}

      {!!(t.specialties || []).length && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {t.specialties.map((s) => <span key={s} className="text-[11px] bg-[#291B2D]/5 text-[#291B2D]/70 rounded-full px-2.5 py-1">{s}</span>)}
        </div>
      )}

      <div className="mt-4 space-y-1.5 text-xs text-[#291B2D]/60">
        {!!states.length && <p className="flex items-center gap-1.5"><MapPin size={12} /> Licensed: {states.join(", ")}</p>}
        {!!(t.languages || []).length && <p className="flex items-center gap-1.5"><Globe size={12} /> {t.languages.join(", ")}</p>}
        {!!(t.insurance || []).length && <p className="flex items-center gap-1.5"><CheckCircle2 size={12} /> {t.insurance.join(", ")}</p>}
      </div>

      <div className="mt-auto pt-4 flex flex-wrap gap-2">
        <Link to={`/therapists/${t.slug || t.id}`} data-testid={`therapist-view-${t.id}`} className="inline-flex items-center gap-1.5 bg-[#F7F2EA] text-[#291B2D] rounded-full px-4 py-2 text-xs font-medium hover:brightness-95">View profile</Link>
        {booking && (
          <a href={booking} target="_blank" rel="noreferrer" data-testid={`therapist-book-${t.id}`}
            className="inline-flex items-center gap-1.5 bg-[#291B2D] text-white rounded-full px-4 py-2 text-xs font-medium hover:brightness-110 transition-smooth">
            <Calendar size={13} /> Book / Website
          </a>
        )}
        {t.email && (
          <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1.5 bg-[#F7F2EA] text-[#291B2D] rounded-full px-4 py-2 text-xs font-medium hover:brightness-95">
            <Mail size={13} /> Email
          </a>
        )}
        {t.phone && (
          <a href={`tel:${t.phone}`} className="inline-flex items-center gap-1.5 bg-[#F7F2EA] text-[#291B2D] rounded-full px-4 py-2 text-xs font-medium hover:brightness-95">
            <Phone size={13} /> Call
          </a>
        )}
      </div>
    </div>
  );
}

function Waitlist() {
  const [status, setStatus] = useState(null);
  const [state, setState] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { api.get("/therapists/waitlist/status").then((r) => setStatus(r.data)).catch(() => setStatus({ on_waitlist: false })); }, []);
  const join = async () => {
    setSubmitting(true);
    try {
      await api.post("/therapists/waitlist", { state, specialty });
      toast.success("You're on the list.");
      setStatus({ on_waitlist: true, record: { state, specialty } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not join waitlist"); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="bg-[#F7F2EA] rounded-3xl p-8 lg:p-10" data-testid="therapist-waitlist-card">
      {status?.on_waitlist ? (
        <div className="flex items-start gap-3">
          <CheckCircle2 size={22} className="text-[#3D6B52] shrink-0 mt-0.5" />
          <div>
            <p className="font-heading text-xl lg:text-2xl">You're on the list.</p>
            <p className="text-sm text-[#291B2D]/70 mt-1">We'll email you the moment providers are available in your area.</p>
          </div>
        </div>
      ) : (
        <>
          <p className="font-heading text-xl lg:text-2xl">Notify me when available</p>
          <p className="text-sm text-[#291B2D]/70 mt-1 mb-4">Tell us where you are and what you're looking for.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
            <input data-testid="therapist-state" placeholder="State (e.g. GA)" value={state} onChange={(e) => setState(e.target.value)} className="bg-white border border-[#291B2D]/10 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#C89B55] text-sm" />
            <input data-testid="therapist-specialty" placeholder="Specialty (optional)" value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="bg-white border border-[#291B2D]/10 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#C89B55] text-sm" />
          </div>
          <button onClick={join} disabled={submitting} data-testid="therapist-waitlist-btn" className="bg-[#291B2D] text-white rounded-full px-6 py-3 hover:brightness-110 disabled:opacity-40 transition-smooth">{submitting ? "Adding…" : "Notify me when available"}</button>
        </>
      )}
    </div>
  );
}

const EMPTY_FILTERS = { state: [], specialty: [], modality: [], insurance: [], language: [] };

export default function Therapists() {
  const { user } = useAuth();
  useSeo({
    title: "Find a Culturally Responsive Therapist | Sisterhood+",
    description: "Browse Sisterhood+'s free directory of licensed, culturally responsive therapists — welcoming women from all backgrounds, with deep experience supporting women of color. Filter by state, specialty, insurance & format.",
    path: "/therapists",
    image: "/og-therapists.png",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const parts = [];
      Object.entries(filters).forEach(([k, arr]) => (arr || []).forEach((v) => parts.push(`${k}=${encodeURIComponent(v)}`)));
      const r = await api.get(`/therapists${parts.length ? "?" + parts.join("&") : ""}`);
      setData(r.data);
    } catch { toast.error("Could not load the directory"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { track("therapists_viewed"); }, []);
  useEffect(() => { load(); }, [load]);

  const facets = data?.facets || { states: [], specialties: [], insurance: [], languages: [], modalities: [] };
  const activeCount = Object.values(filters).reduce((a, arr) => a + (arr?.length || 0), 0);
  const hasDirectory = (data?.items?.length || 0) > 0 || activeCount > 0 || (facets.states.length + facets.specialties.length) > 0;

  const q = query.trim().toLowerCase();
  const items = data?.items || [];
  const visibleItems = q
    ? items.filter((t) => {
        const hay = [
          t.name, t.credentials, t.bio,
          ...(t.specialties || []), ...(t.states || []), ...(t.languages || []),
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
    : items;

  const toggle = (dim, value) => setFilters((f) => {
    const cur = f[dim] || [];
    return { ...f, [dim]: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value] };
  });
  const clearAll = () => setFilters({ ...EMPTY_FILTERS });

  const DIMS = [
    { key: "state", label: "State", facet: "states", fmt: (v) => v },
    { key: "specialty", label: "Specialty", facet: "specialties", fmt: (v) => v },
    { key: "modality", label: "Format", facet: "modalities", fmt: (v) => (v === "telehealth" ? "Telehealth" : v === "in-person" ? "In-person" : v) },
    { key: "insurance", label: "Insurance", facet: "insurance", fmt: (v) => v },
    { key: "language", label: "Language", facet: "languages", fmt: (v) => v },
  ];

  return (
    <div className="min-h-screen bg-[#F7F2EA]">
      <header className="sticky top-0 z-10 bg-[#F7F2EA]/90 backdrop-blur border-b border-[#291B2D]/5">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="font-heading text-xl text-[#291B2D]" data-testid="therapists-home-link">Sisterhood<span className="text-[#C89B55]">+</span></a>
          {user ? (
            <a href="/home" className="text-sm bg-[#291B2D] text-white rounded-full px-4 py-2 hover:brightness-110 transition-smooth" data-testid="therapists-app-link">Go to app</a>
          ) : (
            <div className="flex items-center gap-2 sm:gap-3">
              <a href="/therapists/apply" className="hidden sm:inline text-sm text-[#C89B55] hover:underline font-medium" data-testid="therapists-are-you-therapist-link">Are you a therapist?</a>
              <a href="/login" className="text-sm text-[#291B2D]/70 px-3 py-2 hover:text-[#291B2D]" data-testid="therapists-signin-link">Sign in</a>
              <a href="/get-started" className="text-sm bg-[#291B2D] text-white rounded-full px-4 py-2 hover:brightness-110 transition-smooth" data-testid="therapists-join-link">Join Sisterhood+</a>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="therapists-page">
        <div className="bg-[#291B2D] text-white rounded-3xl p-8 lg:p-12 card-soft">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold flex items-center gap-2"><Users size={13} /> Find a Therapist</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-2 leading-tight">Culturally responsive care.</h1>
        <p className="text-white/75 text-base lg:text-lg mt-4 max-w-3xl">
          Licensed, culturally responsive professionals — welcoming women from all backgrounds, with deep experience supporting women of color. Every provider is vetted before they appear here.
        </p>
      </div>

      {loading && <div className="text-center py-10 text-[#291B2D]/45"><Loader2 className="inline animate-spin" size={20} /></div>}

      {!loading && !hasDirectory && (
        <>
          <p className="text-sm text-[#291B2D]/60">Our vetted directory is launching soon. Join the waitlist and we'll notify you.</p>
          <Waitlist />
        </>
      )}

      {!loading && hasDirectory && (
        <>
          <div className="relative" data-testid="therapist-search">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#291B2D]/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, keyword, or specialty…"
              data-testid="therapist-search-input"
              className="w-full bg-white border border-[#291B2D]/10 rounded-2xl pl-11 pr-11 py-3.5 text-sm outline-none focus:ring-2 focus:ring-[#C89B55] text-[#291B2D] placeholder:text-[#291B2D]/40 card-soft"
            />
            {query && (
              <button onClick={() => setQuery("")} data-testid="therapist-search-clear" aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#F7F2EA] flex items-center justify-center text-[#291B2D]/60 hover:text-[#291B2D]">
                <X size={15} />
              </button>
            )}
          </div>

          <div className="bg-white rounded-2xl p-4 card-soft" data-testid="therapist-filters">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={14} className="text-[#291B2D]/60" />
              <span className="text-xs uppercase tracking-widest font-semibold text-[#291B2D]/60">Filter</span>
              {activeCount > 0 && <span className="text-xs text-[#291B2D]/45">· {activeCount} active</span>}
              {activeCount > 0 && <button onClick={clearAll} data-testid="filter-clear-all" className="ml-auto text-xs text-[#C89B55] hover:underline">Clear all</button>}
            </div>
            <div className="space-y-3">
              {DIMS.map(({ key, label, facet, fmt }) => {
                const opts = facets[facet] || [];
                if (!opts.length) return null;
                return (
                  <div key={key} data-testid={`filter-group-${key}`}>
                    <p className="text-[11px] uppercase tracking-widest text-[#291B2D]/45 font-semibold mb-1.5">{label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {opts.map((o) => {
                        const on = (filters[key] || []).includes(o.value);
                        return (
                          <button key={o.value} onClick={() => toggle(key, o.value)} data-testid={`filter-${key}-${o.value}`}
                            className={`text-xs rounded-full px-3 py-1.5 transition-smooth ${on ? "bg-[#291B2D] text-white" : "bg-[#F9F8F6] text-[#291B2D]/70 hover:bg-[#F7F2EA]"}`}>
                            {fmt(o.value)} <span className={on ? "text-white/70" : "text-[#291B2D]/40"}>({o.count})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-sm text-[#291B2D]/55">{visibleItems.length} therapist{visibleItems.length === 1 ? "" : "s"}{q && ` matching “${query.trim()}”`}</p>

          {visibleItems.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center card-soft" data-testid="therapist-no-match">
              <p className="font-heading text-lg">No matches{q ? " for your search" : " with these filters"}.</p>
              <button onClick={() => { clearAll(); setQuery(""); }} className="mt-3 text-sm text-[#C89B55] hover:underline">Clear {q ? "search" : "filters"}</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {visibleItems.map((t) => <TherapistCard key={t.id} t={t} />)}
            </div>
          )}
        </>
      )}

      <div className="text-center pt-2">
        <a href="/therapists/apply" data-testid="therapist-apply-link" className="text-sm text-[#C89B55] hover:underline font-medium">
          Are you a therapist? Apply to join the directory →
        </a>
      </div>

      <p className="text-xs text-[#291B2D]/50 text-center pt-2" data-testid="therapists-disclaimer">
        Sisterhood+ lists independent providers and is not a referral service or medical provider. Verify credentials with your state board before booking.
      </p>
      </div>
    </div>
  );
}
